import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { emailCampaignPath } from '../crmPaths';
import { CRM_CONTACT_OWNER_OPTIONS } from '../contacts/contacts.queries';
import { CampaignChoice, CampaignSelect, TagSelect } from '../outreach/Pickers';
import { StepIcon } from './StepIcon';
import {
  DAYS,
  DelayUnit,
  describeWorkflowError,
  formatHour,
  LIMITS,
  splitMinutes,
  STEP_META,
  StepDraft,
  stepProblem,
  UNIT_MINUTES,
} from './workflowModel';
import { CRM_QUICK_CAMPAIGN } from './workflows.queries';

/**
 * The panel beside the canvas for the step picked on it: every field the step
 * reads, and what it needs before the workflow can be saved. Changes apply as
 * typed; nothing is sent until the workflow is saved.
 */

interface StepInspectorProps {
  step: StepDraft;
  index: number;
  editable: boolean;
  onChange: (step: StepDraft) => void;
  onClose: () => void;
}

export const InspectorShell = ({
  tone,
  icon,
  overline,
  title,
  onClose,
  children,
}: {
  tone: string;
  icon: React.ReactNode;
  overline: string;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) => (
  <Box
    component="section"
    aria-label={title}
    sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', overflow: 'hidden', boxShadow: '0 8px 30px rgba(15,23,42,.06)' }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', background: `linear-gradient(180deg, ${alpha(tone, 0.07)}, transparent)` }}>
      <Box sx={{ width: 34, height: 34, borderRadius: '10px', display: 'grid', placeItems: 'center', color: tone, bgcolor: alpha(tone, 0.12) }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="overline" sx={{ lineHeight: 1.2, color: 'text.secondary', display: 'block' }}>
          {overline}
        </Typography>
        <Typography sx={{ fontWeight: 650 }}>{title}</Typography>
      </Box>
      <IconButton size="small" aria-label="Close the panel" onClick={onClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
    <Box sx={{ p: 2, display: 'grid', gap: 2 }}>{children}</Box>
  </Box>
);

const NAME_FIELDS = ['{{ recipient.first_name_or_friend }}', '{{ recipient.first_name }}', '{{ recipient.last_name }}'];

/** Chips that add a name field at the end of a text: the only merge fields texts and pushes take. */
const MergeChips = ({ onInsert, disabled }: { onInsert: (tag: string) => void; disabled: boolean }) => (
  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
      Insert
    </Typography>
    {NAME_FIELDS.map((tag) => (
      <Chip
        key={tag}
        size="small"
        variant="outlined"
        disabled={disabled}
        label={tag.replace(/\{\{ recipient\.|\}\}/g, '').trim()}
        onClick={() => onInsert(tag)}
        sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.7rem' }}
      />
    ))}
  </Box>
);

const Note = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="caption" color="text.secondary" component="p" sx={{ lineHeight: 1.45 }}>
    {children}
  </Typography>
);

export const StepInspector = ({ step, index, editable, onChange, onClose }: StepInspectorProps) => {
  const meta = STEP_META[step.type];
  const problem = editable ? stepProblem(step) : null;
  const errorOn = (field: string) => (problem?.field === field ? problem.message : undefined);
  const set = (patch: Partial<StepDraft>) => onChange({ ...step, ...patch });

  return (
    <InspectorShell tone={meta.tone} icon={<StepIcon type={step.type} fontSize={18} />} overline={`Step ${index + 1}`} title={meta.label} onClose={onClose}>
      {step.type === 'DELAY' && <DelayFields step={step} editable={editable} set={set} />}

      {step.type === 'SEND_EMAIL' && <EmailFields step={step} editable={editable} set={set} error={errorOn('emailCampaignId')} />}

      {step.type === 'SEND_SMS' && (
        <>
          <TextField
            label="Text"
            value={step.message ?? ''}
            onChange={(event) => set({ message: event.target.value })}
            multiline
            minRows={4}
            disabled={!editable}
            error={!!errorOn('message')}
            helperText={errorOn('message') ?? `${(step.message ?? '').length} / ${LIMITS.smsBody} · about ${Math.max(1, Math.ceil((step.message ?? '').length / 153))} SMS`}
            inputProps={{ maxLength: LIMITS.smsBody }}
          />
          <MergeChips disabled={!editable} onInsert={(tag) => set({ message: `${(step.message ?? '').trimEnd()} ${tag}`.trim() })} />
          <Note>Goes only to people opted in to texts with a number, and never to do-not-contact. At most 3 workflow texts a day reach any one number.</Note>
        </>
      )}

      {step.type === 'SEND_PUSH' && (
        <>
          <TextField
            label="Title"
            value={step.title ?? ''}
            onChange={(event) => set({ title: event.target.value })}
            disabled={!editable}
            error={!!errorOn('title')}
            helperText={errorOn('title') ?? `${(step.title ?? '').length} / ${LIMITS.pushTitle}`}
            inputProps={{ maxLength: LIMITS.pushTitle }}
          />
          <TextField
            label="Message"
            value={step.message ?? ''}
            onChange={(event) => set({ message: event.target.value })}
            multiline
            minRows={3}
            disabled={!editable}
            error={!!errorOn('message')}
            helperText={errorOn('message') ?? `${(step.message ?? '').length} / ${LIMITS.pushBody}`}
            inputProps={{ maxLength: LIMITS.pushBody }}
          />
          <MergeChips disabled={!editable} onInsert={(tag) => set({ message: `${(step.message ?? '').trimEnd()} ${tag}`.trim() })} />
          <PushPreview title={step.title ?? ''} message={step.message ?? ''} />
          <Note>Reaches people with an app account who are not do-not-contact. It also lands in their in-app inbox.</Note>
        </>
      )}

      {(step.type === 'ADD_TAG' || step.type === 'REMOVE_TAG') && (
        <>
          <TagSelect
            label={step.type === 'ADD_TAG' ? 'Tag to add' : 'Tag to remove'}
            value={step.tagId ?? null}
            valueName={step.tagName}
            onChange={(tag) => set({ tagId: tag?.id ?? null, tagName: tag?.name ?? null })}
            error={errorOn('tagId')}
            disabled={!editable}
          />
          <Note>Recorded on each person's timeline as done by this workflow. System tags are allowed here: automation is what they are for.</Note>
        </>
      )}

      {step.type === 'CREATE_CALL_TASK' && <CallTaskFields step={step} editable={editable} set={set} errorOn={errorOn} />}

      {step.type === 'END' && <Note>Everyone who reaches this step has finished the workflow. Without it, they finish after the last step anyway.</Note>}
    </InspectorShell>
  );
};

/* ------------------------------------------------------------------ *
 * A wait
 * ------------------------------------------------------------------ */

const DelayFields = ({ step, editable, set }: { step: StepDraft; editable: boolean; set: (patch: Partial<StepDraft>) => void }) => {
  const byClock = step.delayMinutes == null;
  const { amount, unit } = splitMinutes(step.delayMinutes);
  const [typed, setTyped] = useState(String(amount));

  const setAmount = (text: string, u: DelayUnit) => {
    setTyped(text);
    const n = Number(text);
    if (Number.isInteger(n) && n > 0) set({ delayMinutes: Math.min(LIMITS.delayMinutes, n * UNIT_MINUTES[u]), delayUntilDow: null, delayUntilHour: null });
  };

  return (
    <>
      <ToggleButtonGroup
        exclusive
        size="small"
        fullWidth
        disabled={!editable}
        value={byClock ? 'until' : 'for'}
        onChange={(_, mode) => {
          if (mode === 'for') set({ delayMinutes: 1440, delayUntilDow: null, delayUntilHour: null });
          if (mode === 'until') set({ delayMinutes: null, delayUntilDow: 2, delayUntilHour: 10 });
        }}
      >
        <ToggleButton value="for">For a time</ToggleButton>
        <ToggleButton value="until">Until a day and hour</ToggleButton>
      </ToggleButtonGroup>

      {!byClock ? (
        <Stack direction="row" spacing={1}>
          <TextField
            label="Wait"
            type="number"
            value={typed}
            disabled={!editable}
            onChange={(event) => setAmount(event.target.value, unit)}
            inputProps={{ min: 1 }}
            sx={{ flex: 1 }}
          />
          <TextField select label="Unit" value={unit} disabled={!editable} onChange={(event) => setAmount(typed, event.target.value as DelayUnit)} sx={{ width: 130 }}>
            {(['minutes', 'hours', 'days', 'weeks'] as const).map((u) => (
              <MenuItem key={u} value={u}>
                {u}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1}>
          <TextField
            select
            label="Day"
            value={step.delayUntilDow ?? 'next'}
            disabled={!editable}
            onChange={(event) => set({ delayUntilDow: event.target.value === 'next' ? null : Number(event.target.value) })}
            sx={{ flex: 1 }}
          >
            <MenuItem value="next">The next day</MenuItem>
            {DAYS.map((day, i) => (
              <MenuItem key={day} value={i}>
                {day}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="At"
            value={step.delayUntilHour ?? 10}
            disabled={!editable}
            onChange={(event) => set({ delayUntilHour: Number(event.target.value) })}
            sx={{ width: 130 }}
          >
            {Array.from({ length: 24 }, (_, h) => (
              <MenuItem key={h} value={h}>
                {formatHour(h)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      )}
      <Note>
        Times are in the organisation's time zone (CRM_WORKFLOW_TIMEZONE, New York unless set): contacts carry none of their own.
      </Note>
    </>
  );
};

/* ------------------------------------------------------------------ *
 * An email
 * ------------------------------------------------------------------ */

const EmailFields = ({ step, editable, set, error }: { step: StepDraft; editable: boolean; set: (patch: Partial<StepDraft>) => void; error?: string }) => {
  const [making, setMaking] = useState(false);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [create, created] = useMutation<{ crmCreateEmailCampaign: CampaignChoice }>(CRM_QUICK_CAMPAIGN);

  const value: CampaignChoice | null = step.emailCampaignId
    ? { id: step.emailCampaignId, name: step.emailCampaignName ?? `Campaign #${step.emailCampaignId}`, subject: step.emailSubject ?? null }
    : null;

  const choose = (campaign: CampaignChoice | null) =>
    set({ emailCampaignId: campaign?.id ?? null, emailCampaignName: campaign?.name ?? null, emailSubject: campaign?.subject ?? null });

  const make = async () => {
    try {
      const { data } = await create({ variables: { input: { name: name.trim(), subject: subject.trim() || null } } });
      if (data) choose(data.crmCreateEmailCampaign);
      setMaking(false);
      setName('');
      setSubject('');
    } catch {
      // Shown below from `created.error`.
    }
  };

  return (
    <>
      <CampaignSelect value={value} onChange={choose} error={error} disabled={!editable} />
      {value && (
        <Link component={RouterLink} to={emailCampaignPath(value.id)} target="_blank" rel="noopener" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem' }}>
          Write or check this email <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
      )}
      {editable && !making && (
        <Button size="small" variant="outlined" onClick={() => setMaking(true)} sx={{ justifySelf: 'start' }}>
          New email for this step
        </Button>
      )}
      {making && (
        <Box sx={{ display: 'grid', gap: 1.25, p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid', borderColor: 'divider' }}>
          <TextField label="Name (only admins see it)" value={name} onChange={(event) => setName(event.target.value)} autoFocus inputProps={{ maxLength: 200 }} />
          <TextField label="Subject" value={subject} onChange={(event) => setSubject(event.target.value)} inputProps={{ maxLength: 300 }} />
          {created.error && <Alert severity="error">{describeWorkflowError(created.error, 'The email could not be made.')}</Alert>}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={() => setMaking(false)} disabled={created.loading}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={make}
              disabled={!name.trim() || created.loading}
              startIcon={created.loading ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              Make draft
            </Button>
          </Stack>
          <Note>It is made as a draft campaign now. Write its body from the link that appears, then come back.</Note>
        </Box>
      )}
      <Note>
        Each person gets the campaign's subject and body, with their own merge fields, through the same delivery as a blast. Its own list is ignored, and a
        campaign a workflow sends cannot also be scheduled as a blast. Only people who can be emailed get it; the rest carry on to the next step.
      </Note>
    </>
  );
};

/* ------------------------------------------------------------------ *
 * A call task
 * ------------------------------------------------------------------ */

interface Owner {
  value: string;
  label: string;
}

const CallTaskFields = ({
  step,
  editable,
  set,
  errorOn,
}: {
  step: StepDraft;
  editable: boolean;
  set: (patch: Partial<StepDraft>) => void;
  errorOn: (field: string) => string | undefined;
}) => {
  const owners = useQuery<{ owners: Array<Owner> }>(CRM_CONTACT_OWNER_OPTIONS);
  const options = (owners.data?.owners ?? []).filter((o) => /^\d+$/.test(o.value));
  const assigned = step.assignToAdminId != null ? String(step.assignToAdminId) : '';

  return (
    <>
      <TextField
        label="Due in (hours)"
        type="number"
        value={step.dueInHours ?? 0}
        disabled={!editable}
        onChange={(event) => set({ dueInHours: event.target.value === '' ? 0 : Math.trunc(Number(event.target.value)) })}
        error={!!errorOn('dueInHours')}
        helperText={errorOn('dueInHours') ?? '0 is due at once; at most 720 (30 days).'}
        inputProps={{ min: 0, max: LIMITS.dueInHours }}
      />
      <TextField
        select
        label="For"
        value={assigned}
        disabled={!editable}
        onChange={(event) => set({ assignToAdminId: event.target.value ? Number(event.target.value) : null })}
        helperText={owners.error ? 'Could not load the point people.' : undefined}
      >
        <MenuItem value="">Anyone on the call queue</MenuItem>
        {assigned && !options.some((o) => o.value === assigned) && <MenuItem value={assigned}>Admin #{assigned}</MenuItem>}
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="What to do on the call"
        value={step.note ?? ''}
        disabled={!editable}
        onChange={(event) => set({ note: event.target.value })}
        multiline
        minRows={3}
        error={!!errorOn('note')}
        helperText={errorOn('note') ?? `${(step.note ?? '').length} / ${LIMITS.note}`}
        inputProps={{ maxLength: LIMITS.note }}
      />
      <Note>A follow-up in the call queue and Follow-ups, on the person's record. Do-not-call people get one too; the queue shows why they cannot be called.</Note>
    </>
  );
};

/** How a push reads on a phone's lock screen, merge fields as written. */
const PushPreview = ({ title, message }: { title: string; message: string }) => (
  <Box aria-hidden sx={{ p: 1.5, borderRadius: 3, background: 'linear-gradient(135deg, #1e293b, #334155)' }}>
    <Box sx={{ p: 1.25, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,.92)', backdropFilter: 'blur(8px)' }}>
      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>ILBP · now</Typography>
      <Typography sx={{ fontWeight: 650, fontSize: '0.85rem' }}>{title || 'Title'}</Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#334155', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {message || 'Message'}
      </Typography>
    </Box>
  </Box>
);
